import { getPracticeId, practiceFaxProfile, practiceProviderAliases } from "../config/practice";

/*
 * Fax work (owner 10-11): "Faxes should have quick filters where staff can
 * sort by a particular provider faxes and work on them ... after they finish
 * working on a fax we should give a note option so that Irma can know what
 * happened on that fax."
 *
 * v2 (api/ringcentral/practice/work.py) gives every get-list row a `work`
 * object (null = open, Unassigned):
 *   { status: "open"|"done", providerid, provider_source, patientid,
 *     patient_source: "ai"|"staff", needs_review, last_action,
 *     last_quick_pick, last_note, last_by, last_at }
 * and the list answer `fax_providers` [{providerid, name}] (active providers
 * staff can pick) and `fax_work_available` (false until migration 005).
 *
 * The filters here run in the browser on the rows fax.jsx already searched
 * and date-filtered, so they combine with the search box. Chip counts are the
 * faxes still to work in those rows.
 *
 * Assignee (owner 10-13, OSA staff manager: "user buckets (not just provider
 * buckets) ... where they can assign faxes to different users"): v2 adds
 * work.assignee_user_id / assignee_name / assignee_source ("staff" | "fax":
 * the fax was addressed to that staff member) after migration 009, and the
 * list answer fax_staff [{user_id, name}], fax_assign_available,
 * fax_can_assign and fax_me (the signed-in login's id). The chip row shows
 * either providers or users (filters.by), the user row with "Assigned to me"
 * and "Unassigned" (counts = faxes still to work).
 *
 * Deleted faxes (owner 10-12: "Some are spam ... they should be allowed to
 * delete — we just need to manage smartly on our end"): staff keep Delete,
 * with an optional reason (FAX_DELETE_REASONS) and note; v2 POST fax/delete
 * logs who / when / why itself. A deleted fax is worked: get-list no longer
 * has it, so it is out of "To work" and every chip count. The "Deleted" view
 * reads v2 GET fax/deleted?days=7|30 (rows with `deleted: true`) so Irma can
 * audit them, and Restore puts one back. The AI's "likely spam" hint
 * (row.ai.junk_hint) only offers a one-click "Delete as spam"; nothing is
 * ever deleted automatically.
 *
 * Owner 10-22 (89 of Dr. Malik's 92 faxes of 30 days were marked done and
 * then deleted, so no view showed them for him): the provider pick narrows
 * Done AND Deleted too, and "All (30 days)" (status "all30") reads v2 GET
 * fax/all: open + done + deleted of the last 30 days, provider-filtered on
 * the server, with the window's per-provider counts. Chip counts are the
 * selected tab's (To work: still to work; Done: done; ...). A fax with no
 * provider whose AI read has not finished (no fax_ai row yet, or pending) is
 * "AI pending", not "No provider" (AI_PENDING; v2 work.provider_key).
 */

// "Mark done" quick picks: the server accepts exactly these (work.QUICK_PICKS).
export const FAX_DONE_PICKS = Object.freeze([
  "Filed to chart",
  "Sent to Athena",
  "Called patient",
  "Forwarded to provider",
  "Duplicate",
  "Not ours",
]);
export const FAX_NOTE_MAX = 500;

// Why a fax was deleted: the server accepts these ids (work.DELETE_REASONS)
// and shows the label in the history.
export const FAX_DELETE_REASONS = Object.freeze([
  { id: "spam", label: "Spam" },
  { id: "not_ours", label: "Not for us" },
  { id: "duplicate", label: "Duplicate" },
]);
export const faxDeleteReasonLabel = (id) => FAX_DELETE_REASONS.find((r) => r.id === id)?.label || "";
// The Deleted view's windows (days received), v2 allows 1-31.
export const FAX_DELETED_DAYS = Object.freeze([7, 30]);

export const FAX_STATUS_FILTERS = Object.freeze([
  { id: "todo", label: "To work" },
  { id: "done", label: "Done" },
  { id: "review", label: "Review" },
  { id: "deleted", label: "Deleted" },
  { id: "all", label: "All" },
  { id: "all30", label: "All (30 days)" },
]);
// The "All (30 days)" window (v2 GET fax/all allows 1-30).
export const FAX_ALL_DAYS = 30;
export const UNASSIGNED = "unassigned";
export const AI_PENDING = "ai_pending";
export const ALL_PROVIDERS = "all";
export const ALL_USERS = "all";
export const ME = "me";
// by: which chip row narrows the list ("provider" | "user")
export const DEFAULT_FAX_FILTERS = Object.freeze({ status: "todo", provider: ALL_PROVIDERS, by: "provider", user: ALL_USERS });

// How the provider was chosen, for the viewer's grey hint.
export const PROVIDER_SOURCE_TEXT = Object.freeze({
  fax: "named on the fax",
  sender: "sent by this provider",
  usual: "the patient's usual provider",
  last_visit: "from the patient's last visit",
  next_visit: "from the patient's next visit",
  staff: "set by staff",
});

const ACTION_TEXT = {
  done: "Marked done",
  reopen: "Reopened",
  note: "Note",
  review: "Flagged for review",
  unreview: "Review cleared",
  provider: "Provider changed",
  patient: "Patient matched",
  filed_emr: "Sent to EMR",
  filed_athena: "Sent to Athena",
  deleted: "Deleted",
  restore: "Restored",
  assignee: "Assigned",
};
export const faxWorkActionText = (action) => ACTION_TEXT[action] || action || "";

export function faxWorkOf(row) {
  const w = row && row.work && typeof row.work === "object" ? row.work : null;
  return {
    status: w?.status === "done" ? "done" : "open",
    providerid: w && w.providerid !== null && w.providerid !== undefined && w.providerid !== "" ? w.providerid : null,
    provider_source: w?.provider_source || null,
    patientid: w?.patientid ?? null,
    patient_source: w?.patient_source || null,
    needs_review: Boolean(w?.needs_review),
    last_action: w?.last_action || null,
    last_quick_pick: w?.last_quick_pick || null,
    last_note: w?.last_note || null,
    last_by: w?.last_by || null,
    last_at: w?.last_at || null,
    assignee_user_id:
      w && w.assignee_user_id !== null && w.assignee_user_id !== undefined && w.assignee_user_id !== ""
        ? w.assignee_user_id
        : null,
    assignee_name: w?.assignee_name || null,
    assignee_source: w?.assignee_source || null,
  };
}

// The assignee key of a row ("12", or "unassigned").
export function faxUserKey(row) {
  const id = faxWorkOf(row).assignee_user_id;
  return id === null ? UNASSIGNED : String(id);
}

// Whether the practice's faxes are read by the AI (config/practice.js
// FAX_PROFILES aiLabels; v2 FAX_AI_PRACTICES).
export function faxAiLabelsOn() {
  try {
    return Boolean(practiceFaxProfile()?.aiLabels);
  } catch (e) {
    return false;
  }
}

const AI_NOT_READ = new Set(["pending", "queued", "processing", "running"]);
// A listed fax whose AI read has not finished: no fax_ai row yet (`ai: null`)
// or one still pending. A row without an `ai` key (an older v2) never counts.
export function faxAiNotRead(row) {
  if (!row || typeof row !== "object" || !Object.prototype.hasOwnProperty.call(row, "ai")) return false;
  const ai = row.ai;
  if (ai === null || ai === undefined) return true;
  return typeof ai === "object" && AI_NOT_READ.has(String(ai.status || "").toLowerCase());
}

// The provider key of a row ("15", "unassigned", or "ai_pending": no
// provider yet and the AI has not read the fax), the practice's id aliases
// applied (9928: 16 -> 15) as the dashboard filter does.
export function faxProviderKey(row, aliases = practiceProviderAliases(), aiOn = faxAiLabelsOn()) {
  const id = faxWorkOf(row).providerid;
  if (id === null) return aiOn && faxAiNotRead(row) ? AI_PENDING : UNASSIGNED;
  const s = String(id);
  return aliases && aliases[s] ? String(aliases[s]) : s;
}

// A row of the Deleted view (v2 GET fax/deleted).
export const faxIsDeleted = (row) => Boolean(row && row.deleted === true);

// "All" is every fax still in the list; deleted ones only show under Deleted
// and "All (30 days)" (all30: every row v2 GET fax/all returned).
export function faxStatusMatches(row, status) {
  if (status === "all30") return Boolean(row);
  if (faxIsDeleted(row)) return status === "deleted";
  if (status === "deleted") return false;
  const w = faxWorkOf(row);
  if (status === "done") return w.status === "done";
  if (status === "review") return w.needs_review;
  if (status === "all") return true;
  return w.status !== "done";
}

export function filterFaxRows(rows, filters = DEFAULT_FAX_FILTERS, aliases = practiceProviderAliases(), me = null) {
  const list = Array.isArray(rows) ? rows : [];
  const status = filters?.status || "todo";
  // the Deleted view narrows by provider only (owner 10-22): its rows are
  // worked, so the assignee pick does not apply there.
  const byUser = filters?.by === "user" && status !== "deleted";
  const provider = filters?.provider || ALL_PROVIDERS;
  let user = filters?.user || ALL_USERS;
  if (user === ME) user = me === null || me === undefined ? "__nobody__" : String(me);
  // combine (the unified Inbox's filter bar): the provider AND the user pick apply together.
  const combine = filters?.combine === true;
  const anyProvider = (!combine && byUser) || provider === ALL_PROVIDERS;
  const anyUser = (!combine && !byUser) || user === ALL_USERS || status === "deleted";
  const aiOn = faxAiLabelsOn();
  return list.filter(
    (r) =>
      faxStatusMatches(r, status) &&
      (anyProvider || faxProviderKey(r, aliases, aiOn) === String(provider)) &&
      (anyUser || faxUserKey(r) === String(user))
  );
}

// The rows the chip counts are made of for a status tab: the tab's own
// (To work counts faxes still to work, Done the done ones, ...).
const countsStatus = (status) => (status && FAX_STATUS_FILTERS.some((s) => s.id === status) ? status : "todo");

/*
 * User chips (owner 10-13): "Assigned to me" first (when the server says who
 * I am), then each staff user with faxes still to work (the selected one even
 * at 0), a login the list names but the staff list does not (left / no fax
 * tab) by its stored name, then Unassigned and All. [{ key, label, count }]
 */
export function faxUserChips(rows, staff, selected = ALL_USERS, me = null, status = "todo") {
  const counts = new Map();
  const names = new Map();
  let total = 0;
  const st = countsStatus(status);
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!faxStatusMatches(r, st)) return;
    const k = faxUserKey(r);
    counts.set(k, (counts.get(k) || 0) + 1);
    if (k !== UNASSIGNED && !names.has(k)) names.set(k, faxWorkOf(r).assignee_name);
    total += 1;
  });
  const chips = [];
  const meKey = me === null || me === undefined || me === "" ? null : String(me);
  if (meKey !== null) chips.push({ key: ME, label: "Assigned to me", count: counts.get(meKey) || 0 });
  const known = new Map((Array.isArray(staff) ? staff : []).map((u) => [String(u.user_id), u.name]));
  known.forEach((name, key) => {
    const count = counts.get(key) || 0;
    if (count > 0 || String(selected) === key) chips.push({ key, label: name, count });
  });
  counts.forEach((count, key) => {
    if (key !== UNASSIGNED && !known.has(key)) chips.push({ key, label: names.get(key) || `User ${key}`, count });
  });
  chips.push({ key: UNASSIGNED, label: "Unassigned", count: counts.get(UNASSIGNED) || 0 });
  chips.push({ key: ALL_USERS, label: "All", count: total });
  return chips;
}

export function faxUserName(staff, id, fallback = "") {
  if (id === null || id === undefined || id === "") return "";
  const u = (Array.isArray(staff) ? staff : []).find((x) => String(x.user_id) === String(id));
  return u ? u.name : fallback || `User ${id}`;
}

/*
 * Chips: one per provider with faxes in `rows` for the selected tab
 * (`status`; To work = still to work), by name (the selected one always
 * shows, even at 0), then AI pending (when any), Unassigned, then All.
 * `serverCounts` ({key: n}, v2 GET fax/all provider_counts) replaces the
 * counting for the All (30 days) view, whose rows the server narrowed.
 * [{ key, label, count }]
 */
export function faxProviderChips(
  rows,
  providers,
  selected = ALL_PROVIDERS,
  aliases = practiceProviderAliases(),
  status = "todo",
  serverCounts = null
) {
  const counts = new Map();
  let total = 0;
  if (serverCounts && typeof serverCounts === "object") {
    Object.entries(serverCounts).forEach(([k, n]) => {
      const v = Number(n) || 0;
      const key = aliases && aliases[k] ? String(aliases[k]) : String(k);
      counts.set(key, (counts.get(key) || 0) + v);
      total += v;
    });
  } else {
    const st = countsStatus(status);
    const aiOn = faxAiLabelsOn();
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      if (!faxStatusMatches(r, st)) return;
      const k = faxProviderKey(r, aliases, aiOn);
      counts.set(k, (counts.get(k) || 0) + 1);
      total += 1;
    });
  }
  const names = new Map((Array.isArray(providers) ? providers : []).map((p) => [String(p.providerid), p.name]));
  const chips = [];
  names.forEach((name, key) => {
    const count = counts.get(key) || 0;
    if (count > 0 || String(selected) === key) chips.push({ key, label: name, count });
  });
  // a provider the list names but the directory does not (left the practice)
  counts.forEach((count, key) => {
    if (key !== UNASSIGNED && key !== AI_PENDING && !names.has(key)) chips.push({ key, label: `Provider ${key}`, count });
  });
  if ((counts.get(AI_PENDING) || 0) > 0 || String(selected) === AI_PENDING) {
    chips.push({ key: AI_PENDING, label: "AI pending", count: counts.get(AI_PENDING) || 0 });
  }
  chips.push({ key: UNASSIGNED, label: "Unassigned", count: counts.get(UNASSIGNED) || 0 });
  chips.push({ key: ALL_PROVIDERS, label: "All", count: total });
  return chips;
}


export function faxProviderName(providers, id) {
  if (id === null || id === undefined || id === "") return "";
  const p = (Array.isArray(providers) ? providers : []).find((x) => String(x.providerid) === String(id));
  return p ? p.name : `Provider ${id}`;
}

/*
 * The filters narrowing the list right now, as removable chips (owner 10-22:
 * a remembered "Assigned: me" silently hid faxes). [{ key: "user" |
 * "provider", label, clear: {...filters} }] in the order shown. `combine`:
 * the unified Inbox (both picks apply); else only the pick of `filters.by`
 * (the provider in the Deleted view).
 */
export function faxActiveFilters(filters, { combine = false, assignOn = false, providers = [], staff = [] } = {}) {
  const f = filters || DEFAULT_FAX_FILTERS;
  const status = f.status || "todo";
  const byUser = !combine && assignOn && f.by === "user" && status !== "deleted";
  const out = [];
  const user = f.user || ALL_USERS;
  if (assignOn && user !== ALL_USERS && status !== "deleted" && (combine || byUser)) {
    let label;
    if (user === ME) label = "Assigned to: me";
    else if (user === UNASSIGNED) label = "Assigned to: nobody";
    else label = `Assigned to: ${faxUserName(staff, user)}`;
    out.push({ key: "user", label, clear: { ...f, user: ALL_USERS } });
  }
  const provider = f.provider || ALL_PROVIDERS;
  if (provider !== ALL_PROVIDERS && (combine || !byUser)) {
    let label;
    if (provider === UNASSIGNED) label = "Provider: none";
    else if (provider === AI_PENDING) label = "Provider: AI pending";
    else label = `Provider: ${faxProviderName(providers, provider)}`;
    out.push({ key: "provider", label, clear: { ...f, provider: ALL_PROVIDERS } });
  }
  return out;
}

// The list's second line for a worked fax: "Done · Called patient — left VM · Irma".
// A deleted fax: "Deleted · Spam — toner ad · Irma"; deleted before the
// delete was logged (10-12) with no history at all: FAX_DELETED_UNRECORDED;
// a later entry (a note added in the Deleted view): "Deleted · Note — ...".
export const FAX_DELETED_UNRECORDED = "Deleted · who and why not recorded (before 10-12)";
export function faxWorkLine(row) {
  const w = faxWorkOf(row);
  if (faxIsDeleted(row) && w.last_action !== "deleted") {
    if (!w.last_action) return FAX_DELETED_UNRECORDED;
    return `Deleted · ${faxWorkLine({ work: row.work })}`;
  }
  if (!w.last_action) return "";
  const head = w.last_action === "done" ? "Done" : faxWorkActionText(w.last_action);
  const what = [head, w.last_quick_pick].filter(Boolean).join(" · ");
  return [what + (w.last_note ? ` — ${w.last_note}` : ""), w.last_by].filter(Boolean).join(" · ");
}

// The AI's "likely spam / not for us" hint ({kind, text}) of a read fax, or
// null. v2 reads it from the label, summary, action and sender the model
// already returns (ai_view.junk_hint): marketing | blank | wrong_recipient.
export function faxJunkHint(row) {
  const ai = row && row.ai && typeof row.ai === "object" ? row.ai : null;
  const h = ai && ai.status === "done" ? ai.junk_hint : null;
  if (!h || typeof h !== "object" || !h.kind) return null;
  return { kind: String(h.kind), text: typeof h.text === "string" && h.text ? h.text : "Likely spam" };
}

// ---- remembered per user (localStorage; private windows / blocked storage
// just start from the defaults) -------------------------------------------
const prefKey = (user) => `fax.workFilters.v1:${getPracticeId()}:${user || "-"}`;
// Owner 10-22: a remembered "Assigned: me" hid most faxes in Inbox without
// anyone noticing. On the first Inbox load per user (this marker absent) a
// saved "me" is reset to anyone, once; a later pick is remembered as before.
const meResetKey = (user) => `fax.workFilters.meReset.v1:${getPracticeId()}:${user || "-"}`;

export function resetSavedAssignedMeOnce(login) {
  try {
    if (window.localStorage.getItem(meResetKey(login))) return false;
    window.localStorage.setItem(meResetKey(login), "1");
    const raw = window.localStorage.getItem(prefKey(login));
    const v = raw ? JSON.parse(raw) : null;
    if (!v || typeof v !== "object" || v.user !== ME) return false;
    window.localStorage.setItem(prefKey(login), JSON.stringify({ ...v, user: ALL_USERS }));
    return true;
  } catch (e) {
    return false;
  }
}

export function loadFaxFilters(user) {
  try {
    const raw = window.localStorage.getItem(prefKey(user));
    const v = raw ? JSON.parse(raw) : null;
    if (!v || typeof v !== "object") return { ...DEFAULT_FAX_FILTERS };
    const status = FAX_STATUS_FILTERS.some((s) => s.id === v.status) ? v.status : DEFAULT_FAX_FILTERS.status;
    // (all30 is remembered too: it reads v2 GET fax/all when opened)
    const provider = typeof v.provider === "string" && v.provider ? v.provider : ALL_PROVIDERS;
    const by = v.by === "user" ? "user" : "provider";
    // not `user`: that is this function's parameter (a const of the same name
    // here would shadow it and put prefKey(user) above in its TDZ)
    const userPick = typeof v.user === "string" && v.user ? v.user : ALL_USERS;
    return { status, provider, by, user: userPick };
  } catch (e) {
    return { ...DEFAULT_FAX_FILTERS };
  }
}

export function saveFaxFilters(user, filters) {
  try {
    window.localStorage.setItem(
      prefKey(user),
      JSON.stringify({
        status: filters?.status || "todo",
        provider: filters?.provider || ALL_PROVIDERS,
        by: filters?.by === "user" ? "user" : "provider",
        user: filters?.user || ALL_USERS,
      })
    );
  } catch (e) {
    // storage blocked: the filters still work for this visit
  }
}
